import React, { useMemo, createContext, useContext } from 'react'
import { ApolloClient, InMemoryCache, ApolloProvider, type DocumentNode } from '@apollo/client'
import { i18n } from '@fs/zion-locale'
import { appConfig } from '@fs/zion-config'
import { getSessionId } from '@fs/zion-session'
import { useFeatureFlag } from '@fs/zion-flags'

import ApolloMultiLink from '../../utils/ApolloMultiLink'
import ZionCMSProvider from '../../providers/ZionCMSProvider'

const hasProcess = typeof process !== 'undefined'

const { BRIGHTSPOT_API_KEY, BRIGHTSPOT_URL } = appConfig
const ENV_BRIGHTSPOT_API_KEY = hasProcess && process.env.FS_CLIENT_APP_BRIGHTSPOT_API_KEY
const ENV_BRIGHTSPOT_URL = hasProcess && process.env.FS_CLIENT_APP_BRIGHTSPOT_URL

export type ContentTypeMap = {
  contentType: string
  renderFunction: ({
    resolvedSite,
    relativeUrl,
    contentType,
    previewId,
  }: {
    resolvedSite: string | null | undefined
    relativeUrl: string | null | undefined
    contentType: string | null | undefined
    previewId: string | null | undefined
  }) => JSX.Element
}

type BrightspotContext = {
  customPageQuery?: DocumentNode
  /**
   * A list of Brightspot sites the BrightspotProvider will be configured to use.
   */
  sites: string[]
  /**
   * List of sites that support subsites.
   */
  subsites: string[]

  /**
   * A map of custom content types to their respective queries.
   */
  customContentTypes: ContentTypeMap[]
}

export const BrightspotContext = createContext<BrightspotContext>({} as BrightspotContext)

type BrightspotProviderProps = {
  /**
   * A list of Brightspot sites for the BrightspotProvider to use.
   */
  sites: string[]
  /**
   * True if the sites need custom content support. If so then '<site>:custom' context will be supported to access the
   * custom content GraphQL.
   */
  hasCustomContent?: boolean
  /**
   * List of sites that support subsites.
   */
  subsites?: string[]

  /**
   * A map of custom content types to their respective queries.
   */
  customContentTypes?: ContentTypeMap[]

  children: React.ReactNode
}

export default function BrightspotProvider({
  sites,
  hasCustomContent = false,
  subsites = [],
  customContentTypes = [],
  children,
}: BrightspotProviderProps): JSX.Element {
  const useEctProxy = useFeatureFlag('ZionCMS_UseEctProxy') || { isOn: false }
  const endpoints = sites.reduce((acc, site) => {
    const baseUrl = BRIGHTSPOT_URL || ENV_BRIGHTSPOT_URL
    acc[site] = `${baseUrl}${site}/graphql/delivery/zion-cms`
    acc[`${site}:loc-evt-cal`] = `${baseUrl}${site}/graphql/delivery/location-event-calendar`
    acc[`${site}:help-ctr`] = `${baseUrl}${site}/graphql/delivery/help-center`
    acc[`${site}:marketing`] = `${baseUrl}${site}/graphql/delivery/marketing`
    if (hasCustomContent) {
      acc[`${site}:custom`] = `${baseUrl}${site}/graphql/delivery/custom-type-graphql`
    }
    return acc
  }, {})

  const client = useMemo(() => {
    const link = new ApolloMultiLink({
      baseUrl: BRIGHTSPOT_URL || ENV_BRIGHTSPOT_URL,
      endpoints,
      useEctProxy: useEctProxy.isOn,
      subsites: subsites || [],
      getContext: () => ({
        headers: {
          'Accept-Language': i18n.language === 'zh-hans' ? 'zh-CN' : i18n.language,
          fssessionid: getSessionId() || '',
          'x-api-key': BRIGHTSPOT_API_KEY || ENV_BRIGHTSPOT_API_KEY,
        },
      }),
    })
    return new ApolloClient({
      link,
      cache: new InMemoryCache(),
      connectToDevTools: process.env.NODE_ENV === 'development',
    })
  }, [endpoints, subsites, useEctProxy.isOn])

  return (
    <ApolloProvider client={client}>
      <BrightspotContext.Provider value={{ sites, subsites, customContentTypes }}>
        <ZionCMSProvider>{children}</ZionCMSProvider>
      </BrightspotContext.Provider>
    </ApolloProvider>
  )
}

export function useBrightspot(): BrightspotContext {
  const context = useContext(BrightspotContext)
  if (!context) {
    throw new Error('useBrightspot must be used within a BrightspotProvider')
  }
  return context
}
